/* eslint-disable */

/**
 * Video helpers, in two unrelated halves. Read this before editing either.
 *
 * ---------------------------------------------------------------------------
 * HALF 1 - File validation (detectFileType / isVideoFile / isValidVideoFile /
 * getVideoMetadata / captureVideoThumbnail). Takes a browser `File`, decodes it.
 *
 * KNOWN COST, NOT YET FIXED: a single uploaded video is decoded FOUR times.
 * `BayFilesProgressModal` calls `isVideoFile`, then `getVideoMetadata`, then
 * `captureVideoThumbnail` (see src/BayFilesProgressModal/index.tsx, around
 * lines 729 / 746 / 799). Both of the latter two open by awaiting
 * `isValidVideoFile`, which itself calls `isVideoFile` AND performs a full
 * `loadedmetadata` + codec check - and then each one loads the file into its
 * own `<video>` and re-checks the codec anyway. Per file that is 3 signature
 * reads, 4 metadata decodes and 5 `isValidCodec` calls. On a 500MB upload the
 * user waits through all of it.
 *
 * If you fix that: `getVideoMetadata` already returns everything the thumbnail
 * step needs (`duration`, `aspectRatio`, `hasValidCodec`). Decode once, pass
 * the result (and ideally the live `<video>`) into the thumbnail step, and drop
 * the two inner `isValidVideoFile` calls. Keep `isValidVideoFile` itself - it is
 * the public yes/no check and ui-design's ReadyToShare.jsx imports it directly.
 *
 * Do NOT "optimize" this by deleting a validation step without moving its
 * check somewhere else. Each layer catches a different failure: signature
 * catches a renamed file, `isValidCodec` catches a container we can't decode,
 * and the `loadedmetadata` timeout catches a file that hangs the decoder.
 *
 * That cost is NOT limited to the design editor. Nothing outside bay-ui imports
 * this half directly - apps get it transitively by mounting
 * `BayFilesProgressModal`, and as of 2026-08-27 that is nine app screens:
 *   client (Office): pages/FilesPage/FilesPage.js, ContactPage Attachments,
 *     ESignaturePage CreatePacketModal/Documents.js, ContactsPage.js,
 *     BlogsPage AddEntries.js, apps/ProfileUpdate/FileUploaderModal.js,
 *     apps/Agents/AgentLogoModal.js, apps/Offices/LogoUpdateModal.js
 *   smc: apps/Main/pages/Listings/components/ListingImagesVideosWidget.js
 * `BayFilesGrid` also mounts the modal but never calls this file itself, and
 * client does not mount `BayFilesGrid` at all - it ships its own copy.
 * So a change here is a nine-screen change. Test more than the editor.
 *
 * ---------------------------------------------------------------------------
 * HALF 2 - Polotno store/template predicates (isVideoElement ...
 * syncPageVideoDuration). Pure functions over plain objects, no DOM, no I/O.
 * These are the ones consumers reach for most; see the export contract below.
 *
 * ---------------------------------------------------------------------------
 * EXPORT CONTRACT (audited 2026-08-27 across bay-ui, client, smc, ui-design,
 * including ui-design.worktrees, excluding vendored bay-ui copies and builds)
 *
 * Exported AND imported by real callers - changing a signature breaks apps:
 *   isVideoFile ................ bay-ui BayPolotnoEditor/utils.js, BayFilesProgressModal
 *   getVideoMetadata ........... bay-ui BayFilesProgressModal
 *   captureVideoThumbnail ...... bay-ui BayFilesProgressModal, ui-design ReadyToShare.jsx
 *   isValidVideoFile ........... ui-design ReadyToShare.jsx
 *   isDemuxerError ............. bay-ui BayFilesProgressModal
 *   templateJsonHasMediaElements ui-design services/modules/listingOverlay.js
 *   storeHasVideoElements ...... bay-ui ImagesUpload, PagesTimeline; smc project.js;
 *                                ui-design EditorLayout.jsx, template.jsx
 *   storeHasAudioElements ...... bay-ui ImagesUpload, PagesTimeline; smc project.js;
 *                                ui-design EditorLayout.jsx
 *   canUseTemplateVideoFeatures  bay-ui ImagesUpload; smc project.js
 *   hasListingOverlayFlag ...... bay-ui BayPolotno/toolbars/Options.jsx; ui-design (4 files)
 *   isSocialPostTemplate ....... bay-ui BayPolotno/toolbars/Options.jsx; ui-design listingOverlay.js
 *   syncPageVideoDuration ...... bay-ui BayPolotno/controls/PagesTimeline.tsx
 *   getVideoDurationFromUrl .... smc AutomatedListingVideosWidget.js
 *
 * Exported with ZERO importers anywhere, kept only because they are re-exported
 * from utilities/index.js and therefore appear in the published bay-email-editor
 * `index.d.ts`. Removing them is a public API change, not a cleanup:
 *   isVideoElement, getPageVideoDuration
 *
 * Module-private on purpose (they used to be exported and nothing imported them).
 * If you need one from outside, export it deliberately and add it to the barrel -
 * don't assume the missing `export` was an oversight:
 *   detectFileType, isValidCodec, hasAnimations, isVideoTemplateElement
 *
 * Related but NOT a duplicate: utilities/fileSignatureUtils.js keeps a second
 * magic-number table for images and documents. It has no video signatures, and
 * the two tables disagree on `52494646` (RIFF) - webp there, AVI here. Don't
 * merge them without resolving that.
 */

const VIDEO_ELEMENT_TYPES = ['video', 'gif'];

// 500MB. Must stay a Number: it was previously the string '524288000', which
// made every `file.size > VIDEO_MAX_SIZE_BYTES` comparison rely on JS coercing
// the string back to a number.
//
// Note that the two `isUploadSection ? VIDEO_MAX_SIZE_BYTES : 5e8` ternaries
// below are decorative - 524288000 and 5e8 are both "500MB" (binary vs decimal,
// a 4.8% difference). Either pick a real per-context limit or drop the ternary;
// right now the flag reads as if it changes the cap and it does not.
const VIDEO_MAX_SIZE_BYTES = 524288000;

/**
 * Checks file signature (magic numbers) to identify actual file type
 * This helps detect files that have been renamed with incorrect extensions
 *
 * Module-private: `isVideoFile` is the only caller. Was exported, nothing
 * imported it. See the export contract at the top of the file.
 *
 * @param {File} file - The file to check
 * @returns {Promise<string>} - Detected file type or null if unknown
 */
const detectFileType = async (file) => {
	// We'll read the first 12 bytes of the file which should be enough for most signatures
	return new Promise((resolve) => {
		const reader = new FileReader();
		reader.onloadend = function (e) {
			if (e.target.readyState === FileReader.DONE) {
				const uint8Array = new Uint8Array(e.target.result);
				const signature = Array.from(uint8Array.slice(0, 12))
					.map((byte) => byte.toString(16).padStart(2, '0'))
					.join('');

				// MP4 signatures
				if (
					signature.startsWith('00000018667479706d703432') || // mp42
					signature.startsWith('00000020667479704d534e56') || // MSNV
					signature.startsWith('0000001c667479706d703432') || // mp42
					signature.startsWith('00000018667479706d703431') || // mp41
					signature.startsWith('00000014667479706973') || // 3gp5
					signature.startsWith('0000001c6674797033677036') || // 3gp6
					signature.startsWith('0000001c667479704d534e56') || // MSNV
					signature.startsWith('0000001c667479706d703432') || // mp42
					signature.startsWith('000000146674797071742020') || // qt
					signature.includes('667479706d703432') || // ftymp42
					signature.includes('667479704d5034') || // ftypMP4
					signature.includes('667479704d534e56') || // ftypMSNV
					signature.includes('667479706973') || // ftyp.is
					signature.includes('6674797033677035')
				) {
					// ftyp3gp5
					resolve('video/mp4');
				}
				// MOV signatures (QuickTime)
				else if (
					signature.includes('667479707174202020') || // ftypqt
					signature.includes('6d6f6f76') || // moov
					signature.startsWith('0000000c6674797071742020') ||
					signature.startsWith('000000146674797071742020')
				) {
					resolve('video/quicktime');
				}
				// AVI signatures
				else if (
					signature.includes('52494646') &&
					signature.includes('41564920')
				) {
					// RIFF and AVI
					resolve('video/avi');
				}
				// DIVX signatures
				else if (
					signature.includes('44495658') ||
					signature.includes('64697678')
				) {
					// DIVX or divx
					resolve('video/divx');
				}
				// FLV signatures
				else if (signature.startsWith('464c5601')) {
					// FLV
					resolve('video/flv');
				}
				// WebM signatures
				else if (signature.startsWith('1a45dfa3')) {
					// WebM
					resolve('video/webm');
				}
				// MKV signatures
				else if (signature.includes('6d6174726f736b61')) {
					// matroska
					resolve('video/x-matroska');
				}
				// WMV signatures
				else if (signature.includes('3026b2758e66cf11')) {
					// WMV
					resolve('video/wmv');
				}
				// Unknown type
				else {
					resolve(null);
				}
			}
		};

		// Read the first 12 bytes of the file
		const blob = file.slice(0, 12);
		reader.readAsArrayBuffer(blob);
	});
};

/**
 * Checks if a file is a supported video file
 * @param {File} file - The file to check
 * @returns {Promise<boolean>} - Whether the file is a supported video type
 */
export const isVideoFile = async (file) => {
	// First check the standard mime type and extension
	const hasVideoExtension =
		file.name.endsWith('.mp4') || file.name.endsWith('.mov');
	const hasVideoMimeType =
		file.type === 'video/mp4' || file.type === 'video/quicktime';

	// If both match, it's likely a valid video file but we'll double-check
	if (hasVideoExtension && hasVideoMimeType) {
		// Still verify the file signature to be sure
		const detectedType = await detectFileType(file);
		return detectedType === 'video/mp4' || detectedType === 'video/quicktime';
	}

	// If there's a mismatch, check the actual file signature
	const detectedType = await detectFileType(file);

	// Only return true if the detected type is one of our supported formats
	if (detectedType === 'video/mp4' || detectedType === 'video/quicktime') {
		console.warn(
			`File ${file.name} has incorrect extension or MIME type, but signature indicates ${detectedType}`,
		);
		return true;
	}

	// Found a different video type that we don't support
	if (detectedType && detectedType.startsWith('video/')) {
		console.error(
			`Unsupported video format detected: ${detectedType} in file ${file.name}`,
		);
		return false;
	}

	return false;
};

/**
 * More strictly checks if a video element can play H.264 content
 *
 * Module-private: called by `isValidVideoFile`, `getVideoMetadata` and
 * `captureVideoThumbnail`. Was exported, nothing imported it.
 *
 * Requires the element to already have loaded metadata - the fallback branch
 * reads `videoWidth`/`videoHeight`/`duration`, which are 0/NaN before
 * `loadedmetadata` fires. Calling this earlier returns a confident `false`.
 *
 * @param {HTMLVideoElement} element - Video element to check
 * @returns {boolean} - Whether the codec is valid
 */
const isValidCodec = (element) => {
	// First check: explicit codec support check for H.264
	const h264Support = element.canPlayType(
		'video/mp4; codecs="avc1.64001E, mp4a.40.2"',
	);
	const quicktimeSupport = element.canPlayType(
		'video/quicktime; codecs="avc1.64001E, mp4a.40.2"',
	);

	// Only accept "probably" as a valid response for strict checking
	if (h264Support !== 'probably' && quicktimeSupport !== 'probably') {
		// If we didn't get a strong confirmation, do additional checks
		// Check if we at least have dimensions and duration, which indicates some level of support
		if (!element.videoWidth || !element.videoHeight || !element.duration) {
			return false;
		}

		// If we have dimensions but no strong codec support, it's questionable
		// We need at least a "maybe"
		if (h264Support !== 'maybe' && quicktimeSupport !== 'maybe') {
			return false;
		}
	}

	return true;
};

/**
 * Checks whether a Polotno element is a video element
 *
 * Exported but imported by NOBODY - internal callers only
 * (`isVideoTemplateElement`, `getPageVideoDuration`). It stays exported because
 * utilities/index.js re-exports it into the published bay-email-editor
 * `index.d.ts`, so dropping it is a public API change, not a cleanup.
 *
 * @param {object} element - Polotno element
 * @returns {boolean} - Whether the element is a video
 */
export const isVideoElement = (element) => {
	return VIDEO_ELEMENT_TYPES.includes(element?.type) || element?.mimeType === 'image/gif';
};

// Module-private. Was exported, nothing imported it.
const hasAnimations = (element) => {
	return Array.isArray(element?.animations) && element.animations.length > 0;
};

// Module-private. Was exported, nothing imported it.
//
// NOTE the asymmetry, it is deliberate: an animated static image counts as
// "video-like" for template/store checks (it needs the video pipeline to
// render), but NOT for `getPageVideoDuration`, which only cares about elements
// that carry a real `duration`. That is why `getPageVideoDuration` filters on
// `isVideoElement` and everything else filters on this.
const isVideoTemplateElement = (element) => {
	return isVideoElement(element) || hasAnimations(element);
};

/**
 * The next three functions overlap heavily and all three have real consumers,
 * so none can just be deleted:
 *
 *   templateJsonHasMediaElements(json)  ==  video elements OR any audio
 *   storeHasVideoElements(store)        ==  the video half only
 *   storeHasAudioElements(store)        ==  the audio half only
 *
 * A Polotno store and a serialized template JSON both expose `.pages` and
 * `.audios`, so the first one works on either shape despite the name. If you
 * consolidate, make the general one the implementation and leave the other two
 * as one-line wrappers - do not change which one the callers get, the video-only
 * and audio-only answers are used to drive different UI in EditorLayout.jsx.
 */
export const templateJsonHasMediaElements = (templateJson) => {
	return Boolean(
		templateJson?.pages?.some?.(
			(page) => Array.isArray(page?.children) && page.children.some(isVideoTemplateElement),
		) || templateJson?.audios?.length > 0
	);
};

export const hasListingOverlayFlag = (item) => {
	return Boolean(
		item?.etc?.isListingOverlay ||
		(item?.etc?.cfg?.order?.itemId ?? item?.cfg?.order?.itemId) === 'listing-overlays',
	);
};

const VIDEO_ENABLED_SOCIAL_PRESET_IDS = new Set([
	'square',
	'1080x1350',
	'story',
	'landscape',
]);

export const isSocialPostTemplate = (item) => {
	const tags = item?.tags$General;
	if (!Array.isArray(tags) || tags.length === 0) {
		const tagKeys = item?.tagKeys;
		if (!Array.isArray(tagKeys) || tagKeys.length === 0) return false;

		return tagKeys.some(
			(tagKey) =>
				typeof tagKey === 'string' &&
				tagKey.trim().toLowerCase() === 'type:social post',
		);
	}

	return tags.some((tag) => {
		if (!tag || typeof tag !== 'object') return false;
		return tag?.tagSet?.name === 'Type' && tag?.name === 'Social Post';
	});
};

export const canUseTemplateVideoFeatures = (item) => {
	const orderItemId = item?.etc?.cfg?.order?.itemId ?? item?.cfg?.order?.itemId;
	const isSupportedSocialPreset = VIDEO_ENABLED_SOCIAL_PRESET_IDS.has(orderItemId);
	return (isSocialPostTemplate(item) || isSupportedSocialPreset) && !hasListingOverlayFlag(item);
};

/**
 * Checks whether a Polotno store contains any video elements
 * @param {object} store - Polotno store
 * @returns {boolean} - Whether any page contains a video element or animation
 */
export const storeHasVideoElements = (store) => {
	return Boolean(
		store?.pages?.some?.((page) => page?.children?.some?.(isVideoTemplateElement)),
	);
};

export const storeHasAudioElements = (store) => {
	const audios = store?.audios;
	return Boolean(audios?.length > 0);
};

/**
 * Longest video on the page, floored at 5000ms.
 *
 * Exported but imported by NOBODY - `syncPageVideoDuration` is the only caller.
 * Kept exported for the same public-API reason as `isVideoElement` above.
 *
 * The 5000 floor is duplicated in `syncPageVideoDuration`'s comparison; if you
 * change it, change both or the sync becomes a no-op for short pages.
 */
export const getPageVideoDuration = (page) => {
	const videoElements = page?.children?.filter?.(isVideoElement) || [];
	if (videoElements.length === 0) return 5000;

	const maxDuration = videoElements.reduce((maxDuration, element) => {
		const duration = Number(element?.duration) || 0;
		return duration > maxDuration ? duration : maxDuration;
	}, 0);

	return Math.max(5000, maxDuration);
};

export const syncPageVideoDuration = (page) => {
	if (!page?.set) return;

	const nextDuration = getPageVideoDuration(page);
	if ((Number(page.duration) || 5000) === nextDuration) return;

	page.set({ duration: nextDuration });
};

/**
 * Checks if an error message indicates a demuxer error (unsupported video format/codec)
 *
 * SINGLE SOURCE OF TRUTH for "was this a format/codec failure". `getVideoMetadata`
 * and `captureVideoThumbnail` both used to hand-roll a two-pattern subset of this
 * list inline, which meant a Firefox `NS_ERROR_DOM_MEDIA_METADATA_ERR` was
 * reported to the user as a generic load failure. Add new patterns HERE, and
 * call this rather than string-matching at a new site.
 *
 * @param {string} errorMessage - Error message to check
 * @returns {boolean} - Whether the error is a demuxer error
 */
export const isDemuxerError = (errorMessage) => {
	if (!errorMessage) return false;

	const errorPatterns = [
		'DEMUXER_ERROR_NO_SUPPORTED_STREAMS',
		"Failed to execute 'appendBuffer'",
		'MEDIA_ERR_SRC_NOT_SUPPORTED',
		'MEDIA_ERR_DECODE',
		'format error',
		'invalid format',
		'Failed to load',
		'The media could not be loaded',
		'AbortError',
		'NS_ERROR_DOM_MEDIA_METADATA_ERR',
		'no supported source',
		'unsupported codec',
		'no decoders for requested formats',
	];

	return errorPatterns.some((pattern) => errorMessage.includes(pattern));
};

/**
 * Verifies if a video file appears valid by performing multiple checks
 * @param {File} file - The video file to check
 * @returns {Promise<boolean>} - Whether the file appears valid
 */
export const isValidVideoFile = async (file, isUploadSection=false) => {
	try {
		// First check if it's a video file with a valid format by checking its signature
		const isValidVideo = await isVideoFile(file);

		if (!isValidVideo) {
			console.error(
				`File ${file.name} is not a supported video format based on signature check`,
			);
			return false;
		}

		// Also check the file size
		if (file.size === 0 || file.size > (isUploadSection ? VIDEO_MAX_SIZE_BYTES : 5e8)) {
			// 500MB
			console.error(
				`File ${file.name} is invalid due to size: ${file.size} bytes`,
			);
			return false;
		}

		// Now attempt to load it in a video element to check codecs
		return new Promise((resolve) => {
			const video = document.createElement('video');
			let blobURL = null;
			let timeoutId = null;

			const cleanup = () => {
				if (timeoutId) clearTimeout(timeoutId);
				if (video) video.remove();
				if (blobURL) {
					try {
						window.URL.revokeObjectURL(blobURL);
					} catch (e) {}
				}
			};

			try {
				blobURL = window.URL.createObjectURL(file);
				video.setAttribute('preload', 'metadata');
				video.setAttribute('muted', 'true');

				// Set up a timeout
				timeoutId = setTimeout(() => {
					console.error(
						`Timeout loading metadata for ${file.name} - likely an unsupported format`,
					);
					cleanup();
					resolve(false);
				}, 5000);

				// Listen for errors
				video.addEventListener('error', (e) => {
					console.error(
						`Error loading video ${file.name}:`,
						(e && e.target && e.target.error && e.target.error.message) ||
							'Unknown error',
					);
					cleanup();
					resolve(false);
				});

				// Check if metadata loads correctly
				video.addEventListener('loadedmetadata', () => {
					// Verify codec compatibility
					const isValid = isValidCodec(video);
					if (!isValid) {
						console.error(`File ${file.name} has an unsupported codec`);
					}
					cleanup();
					resolve(isValid);
				});

				// Start loading
				video.src = blobURL;
			} catch (error) {
				console.error(
					`Exception during video validation for ${file.name}:`,
					error,
				);
				cleanup();
				resolve(false);
			}
		});
	} catch (error) {
		console.error(`Error in isValidVideoFile for ${file.name}:`, error);
		return false;
	}
};

/**
 * Gets the metadata from a video file including duration and aspect ratio.
 * @param {File} file - The video file to extract metadata from.
 * @returns {Promise<Object>} - A Promise that resolves with video metadata (duration, aspectRatio, hasValidCodec).
 */
export const getVideoMetadata = (file, isUploadSection=false) => {
	return new Promise((resolve, reject) => {
		const video = document.createElement('video');
		let videoDuration = 0;
		let videoAspectRatio = 0;
		let blobURL = null;

		try {
			// "Quick" is wrong: isValidVideoFile does a FULL decode (signature read
			// + loadedmetadata + codec check), and everything below then decodes the
			// same file again. This is decode 2 and 3 of 4 for one upload - see the
			// KNOWN COST note at the top of the file before you touch it.
			isValidVideoFile(file, isUploadSection).then((isValid) => {
				if (!isValid) {
					cleanupAndReject(
						new Error(
							'Unsupported video format or codec: DEMUXER_ERROR_NO_SUPPORTED_STREAMS',
						),
					);
					return;
				}

				// If initial validation passed, proceed with metadata extraction
				blobURL = window.URL.createObjectURL(file);
				video.setAttribute('preload', 'metadata');
				video.setAttribute('crossOrigin', 'anonymous');
				video.setAttribute('muted', 'true');
				video.setAttribute('src', blobURL);

				// Wait for metadata to load
				video.addEventListener('loadedmetadata', (event) => {
					try {
						const { videoHeight, videoWidth, duration } = event.target;
						videoDuration = duration;

						videoAspectRatio = videoHeight > 0 ? videoWidth / videoHeight : 0;

						// Do a more thorough codec check
						const hasValidCodec = isValidCodec(video);

						// Clean up
						video.remove();

						if (blobURL) {
							try {
								window.URL.revokeObjectURL(blobURL);
							} catch (e) {
								// Silently handle revocation errors
							}
						}

						// Additional validation for values that indicate proper decoding
						if (!hasValidCodec || !videoDuration || videoAspectRatio <= 0) {
							reject(
								new Error(
									'Unsupported video format or codec: DEMUXER_ERROR_NO_SUPPORTED_STREAMS',
								),
							);
							return;
						}

						resolve({
							duration: parseFloat(videoDuration.toFixed(2)),
							aspectRatio: parseFloat(videoAspectRatio.toFixed(2)),
							hasValidCodec: hasValidCodec,
						});
					} catch (innerError) {
						cleanupAndReject(innerError);
					}
				});

				// Handle video loading errors
				video.addEventListener('error', (e) => {
					let errorMessage = 'Unknown error';
					if (e && e.target && e.target.error) {
						errorMessage = e.target.error.message || e.target.error.code;
						console.error('Video loading error:', e.target.error);
					}

					// Normalize every format/codec failure onto the one message the
					// callers match on. Uses the shared pattern list rather than an
					// inline subset - see `isDemuxerError`.
					if (isDemuxerError(errorMessage)) {
						errorMessage =
							'Unsupported video format or codec: DEMUXER_ERROR_NO_SUPPORTED_STREAMS';
					}

					cleanupAndReject(new Error(`Video loading error: ${errorMessage}`));
				});

				// Set up a timeout to avoid hanging indefinitely
				const timeoutId = setTimeout(() => {
					cleanupAndReject(
						new Error(
							'Video metadata loading timed out - possibly an unsupported format',
						),
					);
				}, 5000); // 5 second timeout

				// Second `loadedmetadata` listener, on purpose: the first one (above)
				// resolves or rejects but never clears this timeout. Both fire, in
				// registration order, so this is safe rather than racy - `timeoutId` is
				// assigned synchronously before any media event can dispatch.
				//
				// Still leaky on the failure paths: the `error` handler and the inner
				// `catch` reject WITHOUT clearing the timeout, so a stray timer fires up
				// to 5s later and calls cleanupAndReject on an already-settled promise.
				// Harmless to the caller, but it re-runs video.remove() and
				// revokeObjectURL. The fix is to clear it inside cleanupAndReject, which
				// needs timeoutId hoisted into that scope.
				video.addEventListener('loadedmetadata', () => {
					clearTimeout(timeoutId);
				});
			});
		} catch (outerError) {
			cleanupAndReject(outerError);
		}

		function cleanupAndReject(error) {
			// Safely clean up resources
			if (video) {
				try {
					video.remove();
				} catch (e) {
					// Silently handle DOM removal errors
				}
			}

			if (blobURL) {
				try {
					window.URL.revokeObjectURL(blobURL);
				} catch (e) {
					// Silently handle revocation errors
				}
			}

			reject(error);
		}
	});
};

/**
 * Captures a video thumbnail as a Base64-encoded image.
 * @param {File} file - The video file to capture a thumbnail from.
 * @param {number} [time=3] - Time in seconds where to capture the thumbnail.
 * @returns {Promise<string>} - A Promise that resolves with the thumbnail's Base64 URL.
 */
export const captureVideoThumbnail = (file, time = 3, isUploadSection = false) => {
	let canvasEle = null;
	let video = null;
	let canvas = null;
	let blobURL = null;

	try {
		// Decode 4 of 4. The caller has almost always just awaited
		// `getVideoMetadata`, which already proved the codec and returned
		// `hasValidCodec` - pass that in instead of re-validating here. See the
		// KNOWN COST note at the top of the file.
		return isValidVideoFile(file, isUploadSection).then((isValid) => {
			if (!isValid) {
				return Promise.reject(
					new Error(
						'Unsupported video format or codec: DEMUXER_ERROR_NO_SUPPORTED_STREAMS',
					),
				);
			}

			// Proceed with thumbnail capture if validation passed
			canvasEle = document.createElement('canvas');
			canvasEle.id = 'video-thumbnail-canvas';
			document.body.appendChild(canvasEle);

			return new Promise((resolve, reject) => {
				try {
					video = document.createElement('video');
					canvas = document.getElementById('video-thumbnail-canvas');

					if (!canvas) {
						throw new Error('Canvas element not found');
					}

					// `return` is load-bearing: without it this fell through on an
					// oversized file, decoded it anyway, and called cleanupAndReject a
					// second time from the seeked/error handlers.
					if (file.size === 0 || file.size > (isUploadSection ? VIDEO_MAX_SIZE_BYTES : 5e8)) {
						cleanupAndReject(new Error('Invalid file size'));
						return;
					}

					const context = canvas.getContext('2d');
					blobURL = window.URL.createObjectURL(file);

					video.setAttribute('preload', 'metadata');
					video.setAttribute('crossOrigin', 'anonymous');
					video.setAttribute('muted', 'true');
					video.setAttribute('src', blobURL);

					// Set up a timeout to avoid hanging indefinitely
					const timeoutId = setTimeout(() => {
						cleanupAndReject(
							new Error(
								'Video thumbnail generation timed out - possibly an unsupported format',
							),
						);
					}, 8000); // 8 second timeout

					// Wait for metadata to load
					video.addEventListener('loadedmetadata', () => {
						try {
							// Do another codec check
							if (!isValidCodec(video)) {
								clearTimeout(timeoutId);
								cleanupAndReject(
									new Error(
										'Unsupported video format or codec: DEMUXER_ERROR_NO_SUPPORTED_STREAMS',
									),
								);
								return;
							}

							video.currentTime = time; // Go to a specific time
						} catch (error) {
							clearTimeout(timeoutId);
							cleanupAndReject(
								new Error(`Failed to process video metadata: ${error.message}`),
							);
						}
					});

					// Capture frame once seeking is complete
					video.addEventListener('seeked', () => {
						try {
							clearTimeout(timeoutId);
							let thumbnailUrl = null;
							video.pause();

							// Check if the aspect ratio and the duration is valid
							const videoDuration = video.duration;
							const videoAspectRatio =
								video.videoHeight > 0
									? video.videoWidth / video.videoHeight
									: 0;
							const hasValidCodec = isValidCodec(video);

							if (
								videoAspectRatio >= 0.01 &&
								videoAspectRatio <= 10 &&
								videoDuration >= 3 &&
								videoDuration <= 301 &&
								hasValidCodec
							) {
								canvas.width = video.videoWidth;
								canvas.height = video.videoHeight;
								context.drawImage(video, 0, 0, canvas.width, canvas.height);

								// Generate thumbnail as Base64 URL
								thumbnailUrl = canvas.toDataURL('image/jpeg', 0.8);
							} else {
								cleanupAndReject(new Error('Invalid video metadata'));
							}

							// Clean up resources and resolve
							cleanupResources();
							resolve(thumbnailUrl);
						} catch (error) {
							cleanupAndReject(
								new Error(`Failed to capture thumbnail: ${error.message}`),
							);
						}
					});

					// Handle video loading errors
					video.addEventListener('error', (e) => {
						clearTimeout(timeoutId);
						let errorMessage = 'Unknown error';
						if (e && e.target && e.target.error) {
							errorMessage = e.target.error.message || e.target.error.code;
							console.error('Thumbnail video loading error:', e.target.error);
						}

						// Same normalization as getVideoMetadata, via the shared list.
						if (isDemuxerError(errorMessage)) {
							errorMessage =
								'Unsupported video format or codec: DEMUXER_ERROR_NO_SUPPORTED_STREAMS';
						}

						cleanupAndReject(new Error(`Video loading error: ${errorMessage}`));
					});
				} catch (error) {
					cleanupAndReject(
						new Error(`Thumbnail generation failed: ${error.message}`),
					);
				}

				function cleanupResources() {
					if (video) {
						try {
							video.remove();
						} catch (e) {
							// Silently handle DOM removal errors
						}
					}

					if (canvas) {
						try {
							canvas.remove();
						} catch (e) {
							// Silently handle DOM removal errors
						}
					}

					if (blobURL) {
						try {
							window.URL.revokeObjectURL(blobURL);
						} catch (e) {
							// Silently handle revocation errors
						}
					}
				}

				function cleanupAndReject(error) {
					cleanupResources();
					reject(error);
				}
			});
		});
	} catch (error) {
		return Promise.reject(
			new Error(`Failed to create canvas element: ${error.message}`),
		);
	}
};

/**
 * Gets the duration of a video from a URL and returns it as a formatted string.
 * Note: Does not set crossOrigin by default to avoid CORS issues when just reading metadata.
 * @param {string} videoUrl - The URL of the video to get duration from.
 * @param {boolean} useCrossOrigin - Whether to set crossOrigin='anonymous' (default: false)
 * @returns {Promise<string>} - A Promise that resolves with the formatted duration (e.g., "1:30").
 */
export const getVideoDurationFromUrl = (videoUrl, useCrossOrigin = false) => {
	return new Promise((resolve, reject) => {
		if (!videoUrl) {
			resolve('0:00');
			return;
		}

		const video = document.createElement('video');
		let timeoutId = null;
		let onLoaded;
		let onError;
		let onDurationChange;

		try {
			video.setAttribute('preload', 'metadata');
			video.setAttribute('muted', 'true');

			// Only set crossOrigin if explicitly requested
			// When not set, the browser can read metadata without enforcing CORS
			// This fixes issues with CDNs that have restrictive CORS policies
			if (useCrossOrigin) {
				video.setAttribute('crossOrigin', 'anonymous');
			}

			const cleanup = () => {
				if (timeoutId) clearTimeout(timeoutId);
				if (video) {
					try {
						// Remove event listeners before clearing src to prevent spurious errors
						video.removeEventListener('loadedmetadata', onLoaded);
						video.removeEventListener('durationchange', onDurationChange);
						video.removeEventListener('error', onError);
						video.src = '';
						video.remove();
					} catch (e) {
						// Silently handle DOM removal errors
					}
				}
			};

			const formatAndResolve = (duration) => {
				const minutes = Math.floor(duration / 60);
				const seconds = Math.floor(duration % 60);
				const formattedDuration = `${minutes}:${seconds.toString().padStart(2, '0')}`;
				cleanup();
				resolve(formattedDuration);
			};

			// Define event handlers before attaching to avoid race conditions
			onLoaded = () => {
				const duration = video.duration;
				const isValidDuration = Number.isFinite(duration) && duration > 0;

				if (isValidDuration) {
					clearTimeout(timeoutId);
					formatAndResolve(duration);
				}
				// If duration is not yet valid, keep listening for durationchange
			};

			// Fallback: durationchange fires when the actual duration becomes known,
			// which can happen after loadedmetadata for certain MP4 encodings
			onDurationChange = () => {
				const duration = video.duration;
				const isValidDuration = Number.isFinite(duration) && duration > 0;

				if (isValidDuration) {
					clearTimeout(timeoutId);
					formatAndResolve(duration);
				}
			};

			onError = (e) => {
				clearTimeout(timeoutId);
				const errorMsg = (e && e.target && e.target.error && e.target.error.message) || 'Unknown error';

				console.error(
					`Error loading video metadata for ${videoUrl}:`,
					errorMsg,
				);
				cleanup();
				resolve('0:00');
			};

			// Attach listeners before setting src to catch fast loadedmetadata events
			video.addEventListener('loadedmetadata', onLoaded);
			video.addEventListener('durationchange', onDurationChange);
			video.addEventListener('error', onError);

			// Set up a timeout to avoid hanging indefinitely
			timeoutId = setTimeout(() => {
				console.warn(
					`Timeout loading metadata for video URL: ${videoUrl} - defaulting to 0:00`,
				);
				cleanup();
				resolve('0:00');
			}, 5000);

			// Set src last to ensure all handlers are ready
			video.src = videoUrl;
			// Explicitly call load() so detached video elements also start fetching metadata
			video.load();
		} catch (error) {
			console.error(
				`Exception during video duration extraction for ${videoUrl}:`,
				error,
			);
			if (timeoutId) clearTimeout(timeoutId);
			if (video) {
				try {
					if (onLoaded) video.removeEventListener('loadedmetadata', onLoaded);
					if (onDurationChange) video.removeEventListener('durationchange', onDurationChange);
					if (onError) video.removeEventListener('error', onError);
					video.src = '';
					video.remove();
				} catch (e) {
					// Silently handle DOM removal errors
				}
			}
			resolve('0:00');
		}
	});
};